/**
 * FG Magnit — auto-scrolling card row.
 *
 * <fg-marquee> wraps a list (.fg-mq__track) of product cards and moves it
 * slowly from right to left in an endless loop.
 *
 *  - Card widths come from CSS (see .fg-mq in fg-magnit.css), so every card keeps
 *    the size it had in the old grid at each breakpoint.
 *  - The loop is seamless: copies of the cards follow the originals, and the row
 *    jumps back by exactly one set's width, which looks identical.
 *  - Motion is sub-pixel (transform), so there is no stepping.
 *  - It eases to a stop while the pointer is over the row, while anything in it has
 *    keyboard focus, while it is being touched or dragged, and when it is off
 *    screen or the tab is hidden. It eases back in afterwards.
 *  - It can be dragged with a mouse, swiped on touch screens, and scrolled
 *    sideways with a trackpad. Clicks still work; a drag never opens a card.
 *  - Keyboard users get a Pause / Play button that appears on focus, and the row
 *    slides a focused card into view.
 *  - With "reduce motion" switched on in the device settings it never moves by
 *    itself, but can still be dragged and swiped.
 *  - Copies are hidden from screen readers and skipped by the Tab key; their
 *    element IDs are made unique so forms and labels keep working.
 */
(() => {
if (customElements.get('fg-marquee')) return;

class FgMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.fg-mq__track');
    if (!this.track || this.live) return;
    this.live = true;

    this.speed = parseFloat(this.dataset.speed) || 34; // pixels per second
    this.offset = 0;
    this.velocity = 0; // 0 = stopped, 1 = full speed
    this.setWidth = 0;
    this.visible = false;
    this.holds = new Set(); // reasons the row is held still
    this.timers = {};
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.frame = this.frame.bind(this);

    this.classList.add('is-live');
    this.scrollLeft = 0;
    this.addToggle();
    this.bindEvents();

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this);
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        this.wake();
      },
      { rootMargin: '100px 0px' }
    );
    this.intersectionObserver.observe(this);

    this.onVisibility = () => this.wake();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onMotionPref = () => {
      this.updateToggle();
      this.wake();
    };
    this.reduced.addEventListener?.('change', this.onMotionPref);
  }

  disconnectedCallback() {
    this.live = false;
    cancelAnimationFrame(this.raf);
    this.raf = null;
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.reduced?.removeEventListener?.('change', this.onMotionPref);
    Object.values(this.timers).forEach(clearTimeout);
  }

  /* ---------------------------------------------------------------- layout */

  originals() {
    return [...this.track.children].filter(
      (li) => !li.hasAttribute('data-mq-copy') && !li.hidden && getComputedStyle(li).display !== 'none'
    );
  }

  layout() {
    const width = this.clientWidth;
    // Only the width matters; ignore height changes (e.g. images finishing loading).
    if (width === this.lastWidth && this.setWidth) return;
    this.lastWidth = width;
    this.track.querySelectorAll('[data-mq-copy]').forEach((li) => li.remove());
    this.setWidth = 0;
    if (!width) {
      this.wake();
      return;
    }
    const items = this.originals();
    if (!items.length) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const first = items[0];
    const last = items[items.length - 1];
    const setWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
    if (setWidth <= gap) return;

    // Enough copies to always cover the visible width plus one full set.
    const copies = Math.max(1, Math.ceil(width / setWidth) + 1);
    const fragment = document.createDocumentFragment();
    for (let c = 1; c <= copies; c += 1) {
      items.forEach((li) => fragment.appendChild(this.copy(li, c)));
    }
    this.track.appendChild(fragment);

    this.setWidth = setWidth;
    this.itemWidth = first.offsetWidth + gap;
    this.offset = this.wrap(this.offset);
    this.paint();
    // Lets a section wire up behaviour on the copies (e.g. colour dots).
    this.dispatchEvent(new CustomEvent('fg-mq:layout', { bubbles: true }));
    this.wake();
  }

  // Rebuild after the visible cards change (e.g. a filter), starting from the first card.
  refresh() {
    this.lastWidth = null;
    this.offset = 0;
    this.layout();
  }

  // Glide exactly one card left (-1) or right (1), lining cards up neatly, then
  // carry on scrolling by itself after a short pause.
  step(dir) {
    if (!this.setWidth || !this.itemWidth) return;
    this.hold('step', 3500);
    const w = this.itemWidth;
    const from = this.offset;
    let to = (Math.round(from / w) + dir) * w;
    if (Math.abs(to - from) < w * 0.25) to += dir * w;
    const dur = this.reduced.matches ? 0 : 450;
    const t0 = performance.now();
    this.stepping = true;
    const tick = (t) => {
      const k = dur ? Math.min(1, (t - t0) / dur) : 1;
      const eased = 1 - Math.pow(1 - k, 3);
      this.offset = this.wrap(from + (to - from) * eased);
      this.paint();
      if (k < 1) requestAnimationFrame(tick);
      else this.stepping = false;
    };
    requestAnimationFrame(tick);
  }

  copy(li, n) {
    const clone = li.cloneNode(true);
    clone.setAttribute('data-mq-copy', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('fg-offers__item--more');

    // Make IDs unique, and point labels / forms / controls at the copy's own elements.
    const map = new Map();
    clone.querySelectorAll('[id]').forEach((el) => {
      const next = `${el.id}-mq${n}`;
      map.set(el.id, next);
      el.id = next;
    });
    if (map.size) {
      const attrs = ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'list'];
      clone.querySelectorAll('*').forEach((el) => {
        attrs.forEach((a) => {
          const v = el.getAttribute(a);
          if (v) el.setAttribute(a, v.split(/\s+/).map((id) => map.get(id) || id).join(' '));
        });
        const modal = el.getAttribute('data-modal');
        if (modal && modal.startsWith('#') && map.has(modal.slice(1))) {
          el.setAttribute('data-modal', `#${map.get(modal.slice(1))}`);
        }
      });
    }
    // Copies are for the eye only: skipped by Tab and screen readers.
    clone
      .querySelectorAll('a, button, input, select, textarea, [tabindex]')
      .forEach((el) => el.setAttribute('tabindex', '-1'));
    return clone;
  }

  wrap(x) {
    if (!this.setWidth) return 0;
    return ((x % this.setWidth) + this.setWidth) % this.setWidth;
  }

  paint() {
    this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  }

  /* --------------------------------------------------------------- motion */

  shouldMove() {
    return (
      this.setWidth > 0 &&
      this.visible &&
      !document.hidden &&
      !this.reduced.matches &&
      this.holds.size === 0
    );
  }

  wake() {
    if (this.raf || !this.live) return;
    if (!this.shouldMove() && this.velocity === 0) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  frame(now) {
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    const target = this.shouldMove() ? 1 : 0;
    // Ease in gently (about half a second), ease out quickly.
    const rate = target ? 2.2 : 9;
    this.velocity += (target - this.velocity) * Math.min(1, dt * rate);
    if (!target && this.velocity < 0.002) this.velocity = 0;

    if (!this.dragging && !this.stepping && this.velocity > 0) {
      this.offset = this.wrap(this.offset + this.speed * this.velocity * dt);
      this.paint();
    }

    if (target || this.velocity > 0) {
      this.raf = requestAnimationFrame(this.frame);
    } else {
      this.raf = null;
    }
  }

  hold(reason, ms) {
    clearTimeout(this.timers[reason]);
    this.holds.add(reason);
    if (ms) this.timers[reason] = setTimeout(() => this.release(reason), ms);
    this.wake();
  }

  release(reason) {
    clearTimeout(this.timers[reason]);
    this.holds.delete(reason);
    this.wake();
  }

  /* ---------------------------------------------------------------- input */

  bindEvents() {
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') this.hold('hover');
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') this.release('hover');
    });

    // Only keyboard focus holds the row still. A tap or click also focuses the
    // card's link or button, and that must not stop the row for good.
    this.addEventListener('focusin', (e) => {
      let keyboard = true;
      try {
        keyboard = e.target.matches(':focus-visible');
      } catch (err) {
        /* older browsers: treat as keyboard */
      }
      if (!keyboard) return;
      this.hold('focus');
      if (e.target.closest('.fg-mq__toggle')) return;
      this.scrollLeft = 0;
      this.reveal(e.target);
    });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.release('focus');
    });

    // Drag (mouse) and swipe (touch). A small dead zone keeps taps and clicks working.
    this.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.target.closest('.fg-mq__toggle') || !this.setWidth) return;
      this.press = { x: e.clientX, y: e.clientY, offset: this.offset, id: e.pointerId, type: e.pointerType };
      this.hold('press');
    });
    this.addEventListener('pointermove', (e) => {
      const p = this.press;
      if (!p || e.pointerId !== p.id) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!this.dragging) {
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
        this.dragging = true;
        this.classList.add('is-dragging');
        try {
          this.setPointerCapture(e.pointerId);
        } catch (err) {
          /* not critical */
        }
      }
      this.offset = this.wrap(p.offset - dx);
      this.paint();
    });
    const end = (e) => {
      const p = this.press;
      if (!p || e.pointerId !== p.id) return;
      if (this.dragging) {
        this.justDragged = true;
        setTimeout(() => (this.justDragged = false), 0);
      }
      this.dragging = false;
      this.press = null;
      this.classList.remove('is-dragging');
      // After a touch, wait a moment before moving again so the visitor can tap a card.
      if (p.type === 'mouse') this.release('press');
      else this.hold('press', 3000);
    };
    this.addEventListener('pointerup', end);
    this.addEventListener('pointercancel', end);
    this.addEventListener(
      'click',
      (e) => {
        if (this.justDragged) {
          e.preventDefault();
          e.stopPropagation();
          this.justDragged = false;
        }
      },
      true
    );
    this.addEventListener('dragstart', (e) => e.preventDefault());

    // Sideways trackpad / shift+wheel scrolling moves the row; vertical scrolling is left alone.
    this.addEventListener(
      'wheel',
      (e) => {
        if (!this.setWidth || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        this.offset = this.wrap(this.offset + e.deltaX);
        this.paint();
        this.hold('wheel', 1500);
      },
      { passive: false }
    );
  }

  // Slide a focused card fully into view.
  reveal(target) {
    const li = target.closest('.fg-mq__track > *');
    if (!li || !this.setWidth) return;
    const width = this.clientWidth;
    const left = li.offsetLeft;
    const right = left + li.offsetWidth;
    if (left >= this.offset && right <= this.offset + width) return;
    this.offset = this.wrap(left - Math.max(0, (width - li.offsetWidth) / 2));
    this.paint();
  }

  /* -------------------------------------------------------- pause toggle */

  addToggle() {
    const label = this.dataset.label || 'products';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fg-mq__toggle';
    btn.addEventListener('click', () => {
      if (this.holds.has('user')) this.release('user');
      else this.hold('user');
      this.updateToggle();
    });
    this.toggle = btn;
    this.toggleLabel = label;
    this.prepend(btn);
    this.updateToggle();
  }

  updateToggle() {
    if (!this.toggle) return;
    const stopped = this.holds.has('user') || this.reduced.matches;
    this.toggle.textContent = stopped
      ? `Start scrolling ${this.toggleLabel}`
      : `Pause scrolling ${this.toggleLabel}`;
    this.toggle.setAttribute('aria-pressed', stopped ? 'true' : 'false');
    this.toggle.hidden = this.reduced.matches;
  }
}

customElements.define('fg-marquee', FgMarquee);
})();
